import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AiComposer } from "@/components/quote/AiComposer";
import { HeaderForm } from "@/components/quote/HeaderForm";
import { LinesEditor } from "@/components/quote/LinesEditor";
import { SummaryPanel } from "@/components/quote/SummaryPanel";
import { SendQuoteDialog } from "@/components/quote/SendQuoteDialog";
import { TransportPanel } from "@/components/quote/TransportPanel";
import { MealsPanel } from "@/components/quote/MealsPanel";
import { CablingRow } from "@/components/quote/CablingRow";
import { CategoryPicker } from "@/components/quote/CategoryPicker";
import { FixedFeeRow } from "@/components/quote/FixedFeeRow";
import { PreLogisticsPanel } from "@/components/quote/PreLogisticsPanel";
import { HistoryBar } from "@/components/quote/HistoryBar";
import { LockedBanner } from "@/components/quote/IssueControls";
import { useUndoable } from "@/lib/useUndoable";

const AUTOSAVE_EVERY = 3;
import { PdfPreview } from "@/components/quote/PdfPreview";
import { useApi, errMsg } from "@/lib/api";
import { newLineId, setMealDefaults } from "@/lib/pricing";
import { autoSchedule, SHOW_TIME } from "@/lib/schedule";
import { openPdf, fetchPdfUrl } from "@/lib/pdf";

const SCHEDULE = ["event_date_end", "setup_date", "setup_time", "teardown_date", "teardown_time"];
const FIELDS = ["client_id", "client", "subject", "category", "location", "location_lat", "location_lon", "distance_km", "fuel_price", "vans", "meals", "cabling_pct", "fixed_fee_on", "fixed_fee", "event_date", "timing", ...SCHEDULE, "show_days", "days", "lines",
  "discount_type", "discount_value", "material_discount", "payment_terms", "deposit_pct", "deposit_method", "validity_days", "notes", "clauses_on", "status", "revision"];

const blankQuote = (s) => ({
  client_id: null, client: {}, subject: "", category: "Service", location: "", location_lat: null, location_lon: null, distance_km: 0, fuel_price: s.default_fuel_price || 0, vans: [], meals: [], cabling_pct: s.default_cabling_pct ?? 10, fixed_fee_on: true, fixed_fee: s.default_fixed_fee || 0,
  event_date: "", timing: "", ...Object.fromEntries(SCHEDULE.map((k) => [k, ""])), show_days: [], days: 1, lines: [],
  discount_type: "percent", discount_value: s.default_discount ?? 0, material_discount: 0, payment_terms: s.default_payment || "", deposit_pct: s.default_deposit_pct ?? 40, deposit_method: s.default_deposit_method ?? "Bonifico immediato", validity_days: s.default_validity || 1,
  notes: s.default_notes || "", status: "bozza", revision: "",
});

const clientTerms = (c, s) => {
  const hasDiscount = (+c.sconto_materiale || 0) > 0 || (+c.sconto_totale || 0) > 0;
  return {
    client_id: c.id, client: c, payment_terms: c.payment_terms || s.default_payment || "",
    material_discount: hasDiscount ? +c.sconto_materiale || 0 : 0,
    discount_type: "percent",
    discount_value: hasDiscount ? +c.sconto_totale || 0 : s.default_discount ?? 0,
  };
};

export default function QuoteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { state } = useLocation();
  const api = useApi();
  const [data, setData] = useState(null);
  const hist = useUndoable(30);
  const { value: q, set: setQ, patch: patchQ, reset: resetQ } = hist;
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const savedSteps = useRef(0);
  const autoTimer = useRef(null);
  const [preview, setPreview] = useState(null);
  const [aiText] = useState(state?.aiText || "");

  useEffect(() => {
    (async () => {
      const [c, k, cl, s] = await Promise.all(["/catalog", "/kits", "/clients", "/settings"].map((p) => api.get(p)));
      setMealDefaults(s.data);
      setData({ catalog: c.data, kits: k.data, clients: cl.data, settings: s.data });
      if (id) { if (q?.id !== id) resetQ((await api.get(`/quotes/${id}`)).data); }
      else resetQ(blankQuote(s.data));
    })().catch((e) => toast.error(errMsg(e, "Errore di caricamento")));
    if (state?.aiText) window.history.replaceState({}, "");
  }, [api, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => setQ((p) => ({ ...p, ...patch }));
  const setLines = (fn) => setQ((p) => ({ ...p, lines: fn(p.lines) }));

  const compose = async (text) => {
    try {
      const r = (await api.post("/ai/compose", { text })).data;
      const matched = r.client_id && data.clients.find((c) => c.id === r.client_id);
      setQ((p) => ({
        ...p,
        subject: r.subject || p.subject, location: r.location || p.location, event_date: r.event_date || p.event_date,
        timing: r.timing || p.timing, days: r.days || p.days,
        ...(r.show_days?.length ? { show_days: r.show_days.map((d) => ({ ...d, time: d.time || SHOW_TIME })) } : {}),
        ...autoSchedule({ ...p, event_date: r.event_date || p.event_date, event_date_end: r.event_date_end || p.event_date_end, show_days: r.show_days?.length ? r.show_days : p.show_days }),
        ...Object.fromEntries(SCHEDULE.filter((k) => r[k]).map((k) => [k, r[k]])),
        client_id: r.client_id || (r.client?.name ? null : p.client_id),
        client: r.client?.name ? r.client : p.client,
        ...(matched ? clientTerms(matched, data.settings) : {}),
        lines: [...p.lines, ...r.lines.map((l) => ({ ...l, id: newLineId() }))],
      }));
      toast.success(`Aggiunte ${r.lines.length} voci dall'AI`);
    } catch (e) { toast.error(errMsg(e, "Composizione AI non riuscita")); }
  };

  const save = async ({ silent = false } = {}) => {
    if (q.status && q.status !== "bozza") return q;
    setSaving(true);
    const steps = hist.steps;
    try {
      const payload = { ...Object.fromEntries(FIELDS.map((f) => [f, q[f]])), deposit_pct: +q.deposit_pct || 0 };
      payload.lines = q.lines.map((l) => ({ ...l, qty: +l.qty || 0, price: +l.price || 0, cost: +l.cost || 0 }));
      const r = q.id ? await api.put(`/quotes/${q.id}`, payload) : await api.post("/quotes", payload);
      const meta = { id: r.data.id, number: r.data.number, code: r.data.code, year: r.data.year, created_at: r.data.created_at, updated_at: r.data.updated_at };
      patchQ((p) => (silent ? { ...p, ...meta } : r.data));
      savedSteps.current = steps;
      setSavedAt(new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }));
      if (!q.id) nav(`/preventivi/${r.data.id}`, { replace: true });
      if (!silent) toast.success(r.data.number ? `Preventivo ${r.data.code} salvato` : "Bozza salvata");
      return r.data;
    } catch (e) {
      toast.error(errMsg(e, silent ? "Salvataggio automatico non riuscito" : "Salvataggio non riuscito"));
      return null;
    } finally { setSaving(false); }
  };

  const locked = !!q && q.status && q.status !== "bozza";
  const hasContent = q && !locked && (q.id || q.client?.name || q.lines?.length);
  const dirty = hist.steps !== savedSteps.current;
  useEffect(() => {
    clearTimeout(autoTimer.current);
    if (!hasContent || saving || hist.steps - savedSteps.current < AUTOSAVE_EVERY) return undefined;
    autoTimer.current = setTimeout(() => { save({ silent: true }); }, 1200);
    return () => clearTimeout(autoTimer.current);
  }, [hist.steps, saving]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const warn = (e) => { if (dirty && hasContent) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, hasContent]);

  const pdf = async (download, plain = false) => {
    const saved = await save();
    if (!saved) return;
    try {
      if (download) await openPdf(api, saved, true, plain);
      else setPreview(await fetchPdfUrl(api, saved.id));
    } catch (e) { toast.error(errMsg(e, "PDF non disponibile")); }
  };

  const [mailing, setMailing] = useState(false);
  const mailList = async () => {
    const saved = await save();
    if (!saved) return;
    setMailing(true);
    try {
      const r = await api.post(`/quotes/${saved.id}/email-material-list`);
      toast.success(`Lista materiale inviata a ${r.data.to}${r.data.cc?.length ? ` (CC: ${r.data.cc.join(", ")})` : ""}`);
    } catch (e) { toast.error(errMsg(e, "Invio email non riuscito")); }
    finally { setMailing(false); }
  };

  const issueQuote = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      const r = await api.post(`/quotes/${saved.id}/issue`);
      resetQ(r.data);
      toast.success(`Preventivo ${r.data.code} inviato e bloccato`);
      await openPdf(api, r.data, true);
    } catch (e) { toast.error(errMsg(e, "Invio non riuscito")); }
  };
  const setCategory = async (category) => {
    if (!locked) { update({ category }); return; }
    try { await api.patch(`/quotes/${q.id}/category`, { category }); patchQ((p) => ({ ...p, category })); toast.success("Categoria aggiornata"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (status) => {
    try { const r = await api.patch(`/quotes/${q.id}/status`, { status }); patchQ((p) => ({ ...p, status: r.data.status })); toast.success("Stato aggiornato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const [revBusy, setRevBusy] = useState(false);
  const createRevision = async () => {
    setRevBusy(true);
    try {
      const r = await api.post(`/quotes/${q.id}/revision`);
      toast.success(`Revisione ${r.data.code} pronta da modificare`);
      nav(`/preventivi/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRevBusy(false); }
  };

  const [sendOpen, setSendOpen] = useState(false);
  const openSend = async () => { if (await save()) setSendOpen(true); };
  const sendQuote = async (email) => {
    try {
      const r = await api.post(`/quotes/${q.id}/send`, { email });
      patchQ((p) => ({ ...p, status: "inviato", client: { ...(p.client || {}), email: r.data.to } }));
      toast.success(`Preventivo inviato a ${r.data.to}${r.data.cc?.length ? ` (CC: ${r.data.cc.join(", ")})` : ""}`);
      return true;
    } catch (e) { toast.error(errMsg(e, "Invio preventivo non riuscito")); return false; }
  };

  const pickClient = (c) => setQ((p) => ({ ...p, ...clientTerms(c, data.settings) }));

  const addClient = async (c) => {
    const r = await api.post("/clients", c);
    setData((d) => ({ ...d, clients: [...d.clients, r.data] }));
    update({ client_id: r.data.id, client: r.data });
    toast.success("Cliente salvato in archivio");
  };

  if (!data || !q) return <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-6 rise" data-testid="quote-editor">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav("/")} data-testid="editor-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <p className="micro-label">{locked ? "Preventivo inviato" : q.id ? "Modifica bozza" : "Nuova bozza"}</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="editor-title">
            {q.number ? <>Preventivo <span className="font-mono text-amber-400">{q.code}</span>{!locked && <span className="ml-2 text-sm text-slate-400">(bozza)</span>}</> : "Bozza di preventivo"}
          </h1>
        </div>
        {!locked && <HistoryBar hist={hist} status={{ saving, dirty, at: savedAt }} />}
        <div className={locked ? "ml-auto" : ""}><CategoryPicker value={q.category} onChange={setCategory} /></div>
      </div>
      {locked && <LockedBanner q={q} onRevision={createRevision} busy={revBusy} />}
      <div className="grid xl:grid-cols-[1fr_360px] gap-6 items-start">
        <fieldset disabled={locked} className="space-y-6 min-w-0" data-testid="editor-main">
          {!locked && <AiComposer onText={compose} initialText={aiText} autoRun={!!aiText} compact />}
          <HeaderForm q={q} update={update} clients={data.clients} onPickClient={pickClient} onAddClient={addClient} />
          <LinesEditor q={q} setLines={setLines} data={data} />
          <CablingRow q={q} update={update} />
          <TransportPanel q={q} update={update} vanTypes={data.settings.van_types || []} />
          <MealsPanel q={q} update={update} />
          <PreLogisticsPanel />
          <FixedFeeRow q={q} update={update} />
        </fieldset>
        <SummaryPanel q={q} update={update} saving={saving} locked={locked} onSave={() => save()} onPdf={pdf} onIssue={issueQuote} onStatus={setStatus} onMail={mailList} mailing={mailing} paymentOptions={data.settings.payment_options || []} />
      </div>
      <PdfPreview url={preview} onClose={() => { URL.revokeObjectURL(preview); setPreview(null); }} onDownload={() => pdf(true)} />
      {sendOpen && <SendQuoteDialog open onOpenChange={setSendOpen} initialEmail={q.client?.email || ""} code={q.code} cc={data.settings.quote_email_cc || []} onSend={sendQuote} />}
    </div>
  );
}
